import React, { ComponentType, useEffect, useRef } from 'react';

import { track } from 'lib/analytics';

import { ACTION_HEADER_PADDING_LEFT, ACTION_HEADER_PADDING_RIGHT, getCellPadding } from '../constants';
import type { CellContentType } from '../types';
import Checkbox from '../../Checkbox';
import Icon from '../../Icon';
import Layout from '../../Layout';
import SortControls from '../../Table/SortControls';
import { ColumnWidthProps, Direction, HeaderWidthProps, SetSortState } from '../../Table/types';
import Text from '../../Text';

export interface HeaderSelectAllConfig {
  value: 'checked' | 'indeterminate' | 'off';
  onToggle: (selected: boolean) => void;
}

export interface HeaderProps extends HeaderWidthProps, ColumnWidthProps {
  columnKey: string;
  value: string;
  activeSortDirection?: Direction;
  sortable: boolean;
  setSortState: SetSortState;
  CustomHeader?: ComponentType;
  isMoney?: boolean;
  isFirstColumn?: boolean;
  align?: 'start' | 'center' | 'end';
  paddingVariant?: 'default' | 'compactRight' | 'compactLeft' | 'noLeft';
  cellContentType?: CellContentType;
  headerSelectAll?: HeaderSelectAllConfig;
}

const Header: React.FC<HeaderProps> = ({
  value,
  sortable,
  activeSortDirection,
  setSortState,
  setColumnWidths,
  getColumnWidth,
  columnKey,
  shrinkWidth,
  flexGrow,
  width,
  maxWidth,
  minWidth,
  CustomHeader,
  paddingVariant,
  cellContentType,
  headerSelectAll,
  align,
  isMoney,
  isFirstColumn
}: HeaderProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const measuredWidth = getColumnWidth(columnKey);
  const isMeasured = measuredWidth !== undefined;
  const isActionHeader = cellContentType === 'action';
  const isCheckHeader = cellContentType === 'check';
  const alignHeaderRight = isMoney && !isFirstColumn;
  const { paddingLeft, paddingRight } = isActionHeader
    ? { paddingLeft: ACTION_HEADER_PADDING_LEFT, paddingRight: ACTION_HEADER_PADDING_RIGHT }
    : isCheckHeader
      ? getCellPadding('compactRight')
      : getCellPadding(paddingVariant);

  const useColumnFlex = flexGrow !== undefined && flexGrow > 0;
  const flexNumericMin = typeof minWidth === 'number' ? minWidth : typeof width === 'number' ? width : undefined;

  useEffect(() => {
    if (!headerRef.current) {
      return;
    }

    const clientWidth = headerRef.current.clientWidth;

    if (measuredWidth === clientWidth) {
      return;
    }

    setColumnWidths({ [columnKey]: clientWidth });
  }, [columnKey, measuredWidth, setColumnWidths, isMeasured]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAsc = activeSortDirection === 'asc';

  /** Fixed gap between header label and sort icon so they stay together regardless of column width/align. */
  const HEADER_LABEL_SORT_GAP = 4;

  const headerFlexStyle: React.CSSProperties = useColumnFlex
    ? {
        minWidth: flexNumericMin !== undefined ? flexNumericMin : undefined,
        flexGrow,
        flexShrink: 1,
        flexBasis: 0,
        maxWidth,
        alignItems: 'center',
        position: 'relative',
        paddingRight,
        display: 'flex',
        boxSizing: 'border-box'
      }
    : {
        width: !flexGrow ? measuredWidth || width : '100%',
        minWidth: 'fit-content',
        maxWidth,
        alignItems: 'center',
        position: 'relative',
        flexGrow: isMeasured || width ? undefined : shrinkWidth ? 0 : flexGrow || 1,
        flexShrink: isMeasured || typeof width === 'number' ? 0 : undefined,
        paddingRight,
        display: 'flex',
        boxSizing: 'border-box'
      };

  return (
    <div className="Table-header" ref={headerRef} style={headerFlexStyle}>
      <div
        ref={contentRef}
        role="button"
        style={{
          cursor: sortable ? 'pointer' : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            align === 'end' || alignHeaderRight ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
          width: '100%',
          minWidth: 0,
          paddingLeft,
          paddingTop: 16,
          paddingBottom: 8
        }}
        onClick={() => {
          if (!sortable) return;

          if (!activeSortDirection) {
            track('Element Interacted', { type: 'iconButton', name: 'table sort desc' });
            setSortState('desc');
          } else if (isAsc) {
            track('Element Interacted', { type: 'iconButton', name: 'table sort asc' });
            setSortState(null);
          } else {
            track('Element Interacted', { type: 'iconButton', name: 'table sort asc' });
            setSortState('asc');
          }
        }}
      >
        {CustomHeader ? (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: HEADER_LABEL_SORT_GAP
            }}
          >
            <CustomHeader />
            <SortControls sortable={sortable} activeSortDirection={activeSortDirection} />
          </div>
        ) : isActionHeader ? (
          <Icon name="ellipsisH" size="xs" color="gray-900" />
        ) : isCheckHeader && headerSelectAll ? (
          <Layout __className="NewTable-checkHeaderHeader" align="center">
            <Checkbox
              label="Select all rows on page"
              value={headerSelectAll.value}
              size="md"
              onChange={({ value }) => headerSelectAll.onToggle(value === 'checked')}
              context="tableHeaderSelectAll"
            />
          </Layout>
        ) : isCheckHeader ? null : (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: HEADER_LABEL_SORT_GAP
            }}
          >
            <Text size="body-xs" color="gray-700" semibold wrap={false} inline>
              {value}
            </Text>
            <SortControls sortable={sortable} activeSortDirection={activeSortDirection} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
